import React, { useState } from "react";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";

import "./NetworkItemCard.scss";
import { NetworkResponse } from "./OrganizationTab";

const MAX_VISIBLE_SECTORS = 3;

interface NetworkItemCardProps {
  networkItem: NetworkResponse;
  loading: "accept" | "deny" | "request" | null;
  onAccept: (actionId: string) => void;
  onDeny: (actionId: string) => void;
  onWithdraw: (partnerId: string) => void;
}

export const NetworkItemCard: React.FC<NetworkItemCardProps> = ({
  networkItem,
  loading,
  onAccept,
  onDeny,
  onWithdraw,
}) => {
  const { partner_data, status } = networkItem;
  const allSectors = partner_data.sectors ?? [];
  const hasHidden = allSectors.length > MAX_VISIBLE_SECTORS;

  const [expanded, setExpanded] = useState(false);

  const displayedSectors = expanded
    ? allSectors
    : allSectors.slice(0, MAX_VISIBLE_SECTORS);
  const hiddenCount = allSectors.length - MAX_VISIBLE_SECTORS;

  return (
    <div className="network-card">
      <div className="network-card__logo">
        {partner_data.logo_uri ? (
          <img src={partner_data.logo_uri} alt={partner_data.title} />
        ) : (
          <div className="network-card__logo-placeholder">
            <i className="pi pi-building" />
          </div>
        )}
      </div>

      <div className="network-card__body">
        <h4 className="network-card__title">{partner_data.title}</h4>

        <div className="network-card__meta">
          <span className="network-card__meta-item">
            <i className="pi pi-building" />
            {partner_data.partner_type.name}
          </span>
          <span className="network-card__meta-item">
            <i className="pi pi-map-marker" />
            {partner_data.country.name}
          </span>
          {partner_data.created && (
            <span className="network-card__meta-item">
              <i className="pi pi-calendar" />
              {partner_data.created}
            </span>
          )}
          {partner_data.size && (
            <span className="network-card__meta-item">
              <i className="pi pi-users" />
              {partner_data.size.name}
            </span>
          )}
          {partner_data.implemented_projects > 0 && (
            <span className="network-card__meta-item">
              <i className="pi pi-folder" />
              {partner_data.implemented_projects} projects
            </span>
          )}
        </div>

        {displayedSectors.length > 0 && (
          <div className="network-card__sectors">
            {displayedSectors.map((sector) => (
              <span key={sector.id} className="network-card__sector-chip">
                {sector.name}
              </span>
            ))}
            {hasHidden && !expanded && (
              <span
                className="network-card__sector-chip network-card__sector-chip--more"
                onClick={() => setExpanded(true)}
              >
                +{hiddenCount}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="network-card__actions">
        {status === "request" && (
          <div
            className="invitationDenyBtn"
            onClick={() => onWithdraw(networkItem.partner_id)}
          >
            <SvgIcon icon="icon-deny" /> Withdraw
          </div>
        )}
        {status === "invitation" && (
          <>
            <div
              className="invitationAcceptBtn"
              onClick={() => onAccept(networkItem.action_id)}
            >
              <SvgIcon icon="icon-accept" /> Accept and join organisation
            </div>
            <div
              className="invitationDenyBtn"
              onClick={() => onDeny(networkItem.action_id)}
            >
              <SvgIcon icon="icon-deny" /> Discard
            </div>
          </>
        )}
      </div>
    </div>
  );
};
